/* eslint-disable */
import { PayloadAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RendererProps } from '@cbojs/pdf-viewer';
import { APIError, api } from '../app/helper';
import { RootState } from '../app/store';
import { IDocCheck, TJobStatus } from './reportsSlice';

const sliceNameSpace = 'active_blobs' as const;

type TPdfBlob = NonNullable<RendererProps['blobs']>[number] & { content: string };

export type TBlobPair = {
  id: string;
  en: TPdfBlob;
  de: TPdfBlob;
  feedback_id: string;
  correctness?: boolean;
};

type TState =
  | { status: 'idle' }
  | {
    status: 'success' | 'loading';
    currentJobId: string;
    data: Array<TBlobPair>;
    focusedBlobId?: string;
    job_status: TJobStatus | 'loading';
  }
  | {
    status: 'error';
    message: string;
    data?: Array<TBlobPair>;
  };

interface IResponse {
  status: string;
  message: string;
  data: {
    job_status: TJobStatus;
    pages_processed: number;
    translations: Array<TBlobPair[]>;
    doc_check?: {
      doc_en_check: IDocCheck,
      doc_de_check: IDocCheck
    };
  };
}

const initialState: TState = { status: 'idle' };

interface IExtra {
  job_status: TJobStatus; // this is intentional as we don't want "loading" to be here
  pages_processed: number;
  doc_check?: {
    en: boolean,
    de: boolean
  };
}

type TPayload = Omit<Extract<TState, { status: 'success' | 'loading' }>, 'status'> & IExtra;

async function fetchAllOrNextMatches(url: string, getState: any, rejectWithValue: any): Promise<TPayload> {
  try {
    const rootState = getState() as RootState;
    if (rootState.jobs_list.status !== 'success') throw new Error('Reports are not fetched yet');

    const { activeReportId } = rootState.jobs_list;
    if (activeReportId === undefined) throw new Error('No active Report ID is set yet');

    const json: IResponse = await api(`${url}${activeReportId}`).then((res) => res.json());
    console.log({ url, json });
    console.log(json.message);

    const { translations, job_status, pages_processed, doc_check } = json.data;
    const result: Array<TBlobPair> = [];
    // eslint-disable-next-line no-restricted-syntax
    for (const translation of translations) {
      if (Array.isArray(translation))
        result.push(...translation);
      else
        result.push(translation);
    }
    return {
      data: result,
      currentJobId: activeReportId,
      job_status,
      pages_processed,
      doc_check: doc_check && { en: doc_check.doc_en_check.status, de: doc_check.doc_de_check.status },
    };
  } catch (error) {
    console.error({ error });
    if (error instanceof APIError) {
      const { details } = error;
      return rejectWithValue(details.message);
    }
    throw error;
  }
}

export const fetchAllMatches = createAsyncThunk<TPayload, void>(
  `${sliceNameSpace}/fetch_translation`,
  async (_, { getState, rejectWithValue }) => fetchAllOrNextMatches('/tcml/fetch_translation/', getState, rejectWithValue),
  {
    condition: (_, { getState }) => {
      const rootState = getState() as RootState;
      if (rootState.jobs_list.status !== 'success') return false;

      const { activeReportId } = rootState.jobs_list;
      if (activeReportId === undefined) return false;

      const { status } = rootState[sliceNameSpace];
      if ((status === 'loading' || status === 'success') && activeReportId === rootState[sliceNameSpace].currentJobId) return false;

      return true;
    },
  },
);

export const fetchNextMatches = createAsyncThunk<TPayload, void>(
  `${sliceNameSpace}/start_translate`,
  async (_, { getState, rejectWithValue }) => fetchAllOrNextMatches('/tcml/start_translate/', getState, rejectWithValue),
  { condition: (_, { getState }) => (getState() as RootState)[sliceNameSpace].status !== 'loading' },
);

export const updateUserFeedback = createAsyncThunk<void, { feedback_id: string, feedback_value: boolean }>(
  `${sliceNameSpace}/update_feedback`,
  (feedback) => api("/tcml/update_feedback", {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(feedback),
  }).then((res) => res.json()).then(console.log)
    .catch((err: APIError) => {
      console.error(err);
      throw err;
    })
);

const { actions, reducer } = createSlice({
  name: sliceNameSpace,
  initialState: initialState as TState,
  reducers: {
    upsertCorrectness(state, { payload: { index, correctness }, }: PayloadAction<{ index: number; correctness: boolean }>) {
      if (state.status === 'success') { state.data[index].correctness = correctness; }
    },
    setFocusedBlobId: (state, { payload: focusedBlobId }: PayloadAction<string>) => {
      if (state.status === 'success') state.focusedBlobId = focusedBlobId;
    },
    clear: () => ({ ...initialState }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllMatches.pending, () => ({
        status: 'loading', data: [], currentJobId: '-1', job_status: 'loading',
      }))
      .addCase(fetchAllMatches.fulfilled, (_, { payload: { data, currentJobId, job_status } }) => ({
        status: 'success', data, currentJobId, job_status,
      })
      )
      .addCase(fetchAllMatches.rejected, (_, { payload }) => ({
        status: 'error', message: (payload as string) || 'unknown error',
      }));
    builder
      // eslint-disable-next-line consistent-return
      .addCase(fetchNextMatches.pending, (state) => {
        if (state.status === 'idle') return { status: 'loading', data: [], currentJobId: '-1', job_status: 'loading' };
        if (state.status === 'success') state.status = 'loading';
        if (state.status === 'error') return { status: 'loading', data: state.data ?? [], currentJobId: '-1', job_status: 'loading' };
      })
      .addCase(fetchNextMatches.fulfilled, (state, { payload: { data, currentJobId, job_status } }) => {
        // if (state.status !== 'loading') throw new Error('pending (status === loading) is always before fulfilled');
        if (state.status === 'idle' || state.status === 'error') throw new Error('this code should not be reached!');
        return {
          status: 'success',
          currentJobId,
          data: [...state.data, ...data],
          job_status,
        };
      })
      .addCase(fetchNextMatches.rejected, (state, { error }) => ({
        status: 'error',
        message: error?.message || 'unknown error',
        data: 'data' in state ? state.data : undefined,
      }));
  },
});

export const selectBlobsSlice = (state: RootState): TState => state[sliceNameSpace];

export const selectBlobsLists = createSelector(
  selectBlobsSlice,
  (slice) => {
    const emptyBlobs: { en: TPdfBlob[]; de: TPdfBlob[] } = { en: [], de: [] };
    return slice.status === 'success' ? slice.data.reduce((acc, blob) => {
      acc.en.push({ ...blob.en, id: blob.id });
      acc.de.push({ ...blob.de, id: blob.id });
      return acc;
    }, emptyBlobs) : emptyBlobs;
  }
);

export const selectFocusedBlobId = (state: RootState): string | undefined => {
  const slice = state[sliceNameSpace];
  if (slice.status === 'success') return slice.focusedBlobId;
  return undefined;
};

export const selectMatchBlobByIndex = (state: RootState, index: number): TBlobPair | undefined => {
  const slice = state[sliceNameSpace];
  if (slice.status === 'success') return slice.data[index];
  return undefined;
};

export const { upsertCorrectness, setFocusedBlobId, clear } = actions;

export default { reducer, namespace: sliceNameSpace };
